import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [deliverables, setDeliverables] = useState([]);
  const [payments, setPayments] = useState([]);
  const [po, setPO] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/orders/${id}`);
    setO(data);
    api.get(`/deliverables?order_id=${id}`).then((r) => setDeliverables(r.data));
    api.get("/payments").then((r) => setPayments(r.data.filter((p) => p.order_id === id)));
    if (data.propay_po_id) api.get("/purchase-orders").then((r) => setPO(r.data.find((x) => x.id === data.propay_po_id)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!o) return <div>Loading…</div>;

  const invite = async () => { await api.post(`/orders/${id}/invite-influencer`); toast.success("Invited"); load(); };
  const issuePO = async () => {
    try { await api.post(`/orders/${id}/issue-po`, {}); toast.success("PO issued"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Cannot issue PO — verify KYS first"); }
  };

  return (
    <div className="space-y-6">
      <Section title={`${o.campaign} — ${o.order_number}`} subtitle={`${o.tenant_name} → PROPAY GLOBAL PRIVATE LIMITED → ${o.influencer_name}`}
        right={<StatusPill status={o.status} />} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Overview */}
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Order details</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-slate-500 text-xs">FMCG PO</div><div className="font-mono">{o.fmcg_po_number}</div></div>
              <div><div className="text-slate-500 text-xs">PO date</div><div>{(o.fmcg_po_date || "").slice(0, 10)}</div></div>
              {o.fmcg_po_file_url && (
                <div className="col-span-2">
                  <div className="text-slate-500 text-xs">Attached PO file</div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL || ""}${o.fmcg_po_file_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[#0052FF] hover:underline text-sm font-semibold">
                    📎 {o.fmcg_po_filename || "Download PO"}
                  </a>
                </div>
              )}
              <div><div className="text-slate-500 text-xs">Order value</div><div className="font-semibold">{o.currency} {Number(o.order_value).toLocaleString()}</div></div>
              <div><div className="text-slate-500 text-xs">Currency</div><div>{o.currency}</div></div>
              <div><div className="text-slate-500 text-xs">FMCG payment terms</div><div>{o.customer_terms_days} days net</div></div>
              <div><div className="text-slate-500 text-xs">Influencer payment terms</div><div>{o.influencer_payment_terms_days} days</div></div>
              <div className="col-span-2"><div className="text-slate-500 text-xs">Primary FMCG approver</div><div>{o.primary_contact_name} · {o.primary_contact_email}</div></div>
              {o.advance_required && (
                <div className="col-span-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                  <div className="text-xs font-bold uppercase tracking-widest">Advance pre-authorised</div>
                  <div className="text-sm mt-0.5">
                    {o.advance_amount ? `${o.currency} ${Number(o.advance_amount).toLocaleString()}` : `${o.advance_percentage}%`} of the back-to-back PO — released
                    {" "}<b>{
                      { on_po_issue: "immediately on PO issue",
                        on_po_acceptance: "after influencer accepts PO",
                        on_progress_25: "when 25% of order lines are approved",
                        on_progress_50: "when 50% of order lines are approved",
                        on_progress_75: "when 75% of order lines are approved",
                        on_delivered: "only after full delivery is approved",
                      }[o.advance_trigger] || "on PO issue"
                    }</b>.
                    {o.advance_triggered_at && <span className="ml-1 text-emerald-700">Fired.</span>}
                  </div>
                  {o.advance_reason && <div className="text-xs text-emerald-800/80 mt-0.5">Reason: {o.advance_reason}</div>}
                </div>
              )}
            </div>
            <div className="mt-5">
              <div className="text-slate-500 text-xs mb-2">Services</div>
              <ul className="text-sm space-y-1">
                {(o.services || []).map((s, i) => (
                  <li key={i} className="flex justify-between border-b py-1">
                    <span>{s.type} × {s.count} — {s.description}</span>
                    <span className="font-mono">{o.currency} {Number(s.fee).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
            {o.notes && <div className="mt-4 bg-slate-50 rounded-lg p-3 text-sm"><b>Notes:</b> {o.notes}</div>}
          </div>

          {/* PO issued */}
          {po && (
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-heading font-bold text-slate-900 mb-2">Back-to-back Purchase Order</h3>
              <div className="text-xs text-slate-500 mb-3">Issued by <b>{po.issued_by_entity}</b> · GSTIN {po.issued_by_gstin}</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><div className="text-slate-500 text-xs">PO number</div><div className="font-mono">{po.po_number}</div></div>
                <div><div className="text-slate-500 text-xs">Amount payable to influencer</div><div className="font-semibold text-emerald-700">{po.currency} {Number(po.amount).toLocaleString()}</div></div>
                <div><div className="text-slate-500 text-xs">Terms</div><div>{po.payment_terms_days} days</div></div>
                <div><div className="text-slate-500 text-xs">Status</div><StatusPill status={po.status} /></div>
              </div>
            </div>
          )}

          {/* Deliverables */}
          {deliverables.length > 0 && (
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-heading font-bold text-slate-900 mb-3">Deliverables</h3>
              <ul className="space-y-3 text-sm">
                {deliverables.map((d) => (
                  <li key={d.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{d.ai?.type?.replace(/_/g, " ")}</div>
                        <div className="text-xs text-slate-500">{d.caption}</div>
                        {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[#0052FF] text-xs underline break-all">{d.url}</a>}
                        {d.ai?.summary && <div className="text-xs text-slate-600 mt-1 italic">AI: {d.ai.summary}</div>}
                      </div>
                      <StatusPill status={d.status} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Payments */}
          {payments.length > 0 && (
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-heading font-bold text-slate-900 mb-3">Payments</h3>
              <ul className="text-sm space-y-2">
                {payments.map((p) => (
                  <li key={p.id} className="flex justify-between border-b py-2">
                    <div>
                      <div className="font-semibold capitalize">{p.type}</div>
                      <div className="text-xs text-slate-500">{p.currency} {Number(p.amount).toLocaleString()} {p.early_payment_fee ? `· Early fee ${p.currency} ${Number(p.early_payment_fee).toLocaleString()}` : ""}</div>
                    </div>
                    <StatusPill status={p.status} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl border p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Procurement actions</div>
            {["procurement_desk", "propay_admin"].includes(user.role) && (
              <div className="space-y-2">
                {o.status === "submitted" && <Button className="w-full" variant="outline" onClick={invite}>Invite influencer</Button>}
                {(o.status === "onboarding" || o.status === "submitted") && <Button className="w-full bg-[#0052FF] hover:bg-[#0043D9]" onClick={issuePO}>Issue back-to-back PO</Button>}
                {o.status === "po_issued" && <div className="text-xs text-emerald-700">PO issued. Influencer must accept.</div>}
              </div>
            )}
            {user.role === "customer" && <div className="text-xs text-slate-500">You'll be notified when the influencer uploads deliverables or requests an advance.</div>}
            {user.role === "influencer" && <div className="text-xs text-slate-500">Head to Deliverables to submit your work, or Payments to request an advance.</div>}
          </div>
          <Link to={user.role === "customer" ? "/customer/orders" : user.role === "influencer" ? "/influencer" : `/${user.role === "propay_admin" ? "admin" : "procurement"}/orders`} className="text-sm text-slate-500 hover:underline">← Back to orders</Link>
        </div>
      </div>
    </div>
  );
}
